import { Injectable, inject } from '@angular/core';
import { HashMap, TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { LocalStorageService } from './local-storage.services';

const APP_LANG_STORAGE_KEY = 'APP_LANG';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private readonly transloco = inject(TranslocoService);
  private readonly localStorageService = inject(LocalStorageService);

  setActiveLanguage(lang?: string): void {
    const availableLangs = this.transloco.getAvailableLangs() as string[];
    const languageFromLocalStorage = this.getStoredLanguage();

    const targetLang =
      lang && availableLangs.includes(lang)
        ? lang
        : (languageFromLocalStorage ??
          this.getBrowserOrFallbackLanguage(availableLangs));

    this.transloco.setActiveLang(targetLang);
    this.saveLanguage(targetLang);
  }

  getActiveLanguage(): string {
    return this.transloco.getActiveLang();
  }

  getAvailableLanguages(): string[] {
    return this.transloco.getAvailableLangs() as string[];
  }

  async loadAvailableLanguages(): Promise<void> {
    await Promise.all(
      this.getAvailableLanguages().map(async (lang) =>
        firstValueFrom(this.transloco.load(lang)),
      ),
    );
  }

  getStoredLanguage(): string | null {
    return this.localStorageService.getItem(APP_LANG_STORAGE_KEY);
  }

  saveLanguage(lang: string): void {
    this.localStorageService.setItem(APP_LANG_STORAGE_KEY, lang);
  }

  private getBrowserOrFallbackLanguage(availableLangs: string[]): string {
    return (
      availableLangs.find((lang) => navigator.languages.includes(lang)) ??
      environment.general.internationalFallbackLanguage
    );
  }

  translateOnRuntime(translationKey: string, params?: HashMap): string {
    const translation = this.transloco.translate(translationKey, params);
    if (translationKey === translation) {
      const parts = translationKey.split('.');
      return parts[parts.length - 1];
    }
    return translation;
  }
}
